import * as React from "react";
import type { AdChannel } from "./types";
import { logTrackingDebug } from "../debug/debugLog";

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    gtag?: Gtag;
  }
}

function gtag(...args: unknown[]): void {
  if (typeof window === "undefined") {
    return;
  }
  const [command, name, params] = args;
  // Keys only for user_data: the value is the user's email.
  logTrackingDebug(
    "googleAds",
    `${command} ${name}`,
    name === "user_data" ? Object.keys((params as object) ?? {}) : params,
  );
  if (!window.gtag) {
    logTrackingDebug("googleAds", "gtag missing: Google tag not on the page");
    return;
  }
  window.gtag(...args);
}

/**
 * Google Ads tag (gtag.js). The <head> snippet loads the Google tag and
 * configs the account on each full page load: that stores the click ID
 * (`gclid` / `gbraid` / `wbraid`) from the landing URL in the `_gcl_*` cookies.
 *
 * The one channel that sends the purchase from the browser: the server-side
 * upload needs Google Ads API access we don't have yet (ad-tracking-plan.md,
 * Phase 4). `transaction_id` = order ID, so a reload of the success page, and
 * later the server upload, dedup against it. First purchases only, like the
 * server-side Purchase of the other channels (no upsells).
 */
export function createGoogleAdsChannel(conversionID: string, purchaseLabel: string): AdChannel {
  // Google hands the ID out both ways: "AW-123" (tag) and "123" (conversion ID).
  const tagID = conversionID.startsWith("AW-") ? conversionID : `AW-${conversionID}`;

  return {
    name: "googleAds",

    headComponents: () => [
      <script
        key="google-ads-gtag"
        async
        src={`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(tagID)}`}
      />,
      <script
        key="google-ads-init"
        dangerouslySetInnerHTML={{
          __html: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config',${JSON.stringify(tagID)},{allow_enhanced_conversions:true});`,
        }}
      />,
    ],

    identify: ({ email }) => {
      // Enhanced conversions: gtag hashes the email in the browser and sends it
      // with later conversions on this page load.
      gtag("set", "user_data", { email: email.trim().toLowerCase() });
    },

    track: (e) => {
      switch (e.name) {
        case "purchase":
          if (e.properties.purchaseProductType !== "initial") {
            return;
          }
          gtag("event", "conversion", {
            send_to: `${tagID}/${purchaseLabel}`,
            value: e.properties.value,
            currency: e.properties.currency,
            transaction_id: e.properties.orderID,
          });
          return;
      }
    },
  };
}
